import React, { useState, useEffect, useRef } from 'react';
import { TrendingUp, Shield, Users, Clock } from 'lucide-react';

export const AnimatedStats = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  const stats = [
    {
      icon: TrendingUp,
      value: 59.90,
      prefix: 'R$ ',
      suffix: '/mês',
      label: 'Investimento Acessível',
      decimals: 2
    },
    {
      icon: Shield,
      value: 8,
      prefix: '',
      suffix: '',
      label: 'Benefícios Inclusos',
      decimals: 0
    },
    {
      icon: Clock,
      value: 24,
      prefix: '',
      suffix: 'h',
      label: 'Telemedicina Disponível',
      decimals: 0
    },
    {
      icon: Users,
      value: 5,
      prefix: 'até ',
      suffix: '',
      label: 'Pessoas Cobertas',
      decimals: 0
    }
  ];

  useEffect(() => {
    const currentSection = sectionRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1, rootMargin: '50px' } // Trigger earlier, with 50px margin
    );

    if (currentSection) {
      observer.observe(currentSection);
    }

    return () => {
      if (currentSection) {
        observer.unobserve(currentSection);
      }
    };
  }, []);

  const AnimatedNumber = ({ value, decimals, prefix, suffix }) => {
    const [displayValue, setDisplayValue] = useState(0);

    useEffect(() => {
      if (!isVisible) return;

      let startTime;
      let animationFrameId;
      const duration = 2000; // 2 seconds animation
      const startValue = 0;
      const endValue = value;

      const animate = (currentTime) => {
        if (!startTime) startTime = currentTime;
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Easing function (ease-out)
        const easeOut = 1 - Math.pow(1 - progress, 3);
        const current = startValue + (endValue - startValue) * easeOut;

        setDisplayValue(current);

        if (progress < 1) {
          animationFrameId = requestAnimationFrame(animate);
        }
      };

      animationFrameId = requestAnimationFrame(animate);
      
      return () => {
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId);
        }
      };
    }, [value, isVisible]);

    return (
      <span>
        {prefix}
        {decimals > 0 ? displayValue.toFixed(decimals) : Math.floor(displayValue)}
        {suffix}
      </span>
    );
  };

  return (
    <section ref={sectionRef} className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0A1E2E] relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-96 h-96 bg-[#00E5CC] rounded-full -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#00E5CC] rounded-full translate-x-1/2 translate-y-1/2"></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            Números que Falam por Si
          </h2>
          <p className="text-[#00E5CC]/90 text-lg">
            Proteção completa com transparência total
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div
                key={index}
                className="text-center transform transition-all duration-500 hover:scale-110"
                style={{
                  animation: isVisible ? `fadeInUp 0.6s ease-out ${index * 0.1}s both` : 'none'
                }}
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#00E5CC]/20 backdrop-blur-sm rounded-2xl mb-4 group-hover:bg-[#00E5CC]/30 transition-colors border border-[#00E5CC]/30">
                  <Icon className="w-8 h-8 text-[#00E5CC]" />
                </div>
                <div className="text-4xl lg:text-5xl font-bold text-white mb-2">
                  {isVisible ? (
                    <AnimatedNumber
                      value={stat.value}
                      decimals={stat.decimals}
                      prefix={stat.prefix}
                      suffix={stat.suffix}
                    />
                  ) : (
                    <span>{stat.prefix}0{stat.suffix}</span>
                  )}
                </div>
                <div className="text-[#00E5CC]/80 font-medium">
                  {stat.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
};
